/**
 * Every camera move in one place, so `prefers-reduced-motion` is honoured once.
 *
 * `tokens.css` collapses every CSS duration to zero under that media query, and
 * a map animated by WebGL is the one surface that rule cannot reach — so the
 * check moves here, into the single helper every `setCenter`/`fitBounds` call
 * passes through (design §1.5).
 */

import type { FitBoundsOptions, LngLatBounds, Map as MapGLMap, Padding } from '@2gis/mapgl/types';

import { prefersReducedMotion } from '../lib/reducedMotion';
import { boundsOf, type LngLat } from './objects';

const DEFAULT_MS = 320;

/** Animation options for any camera call — `{duration: 0}` when motion is reduced. */
export function ease(durationMs: number = DEFAULT_MS): { animation: { duration: number } } {
  return { animation: { duration: prefersReducedMotion() ? 0 : durationMs } };
}

/** The CARD mode's framing (`mode` absent or «card»): the walk with air. */
export const CARD_PADDING: Partial<Padding> = { top: 24, right: 24, bottom: 24, left: 24 };

/**
 * Frames the whole walk.
 *
 * `maxZoom` matters for the degenerate case: a route of one point has a box of
 * zero size, and `fitBounds` on it zooms to the maximum the style has, which
 * puts the visitor inside a building. Nothing happens at all when there are no
 * points — leaving the camera where it is beats moving it to the null island.
 */
export function fitToRoute(
  map: MapGLMap,
  points: readonly LngLat[],
  padding: Partial<Padding> = CARD_PADDING,
  options: { maxZoom?: number; durationMs?: number } = {},
): void {
  const bounds = boundsOf(points);
  if (!bounds) return;
  const fit: FitBoundsOptions = {
    padding,
    maxZoom: options.maxZoom ?? 17,
    ...ease(options.durationMs),
  };
  map.fitBounds(bounds as LngLatBounds, fit);
}

/** Keeps the visitor's own position centred while the guide is leading. */
export function follow(map: MapGLMap, at: LngLat, zoom?: number): void {
  map.setCenter(at, ease().animation);
  if (typeof zoom === 'number' && Math.abs(map.getZoom() - zoom) > 0.01) {
    map.setZoom(zoom, ease().animation);
  }
}
